import { useMutation } from '@tanstack/react-query';
import download from 'downloadjs';

import { useRequest } from '@frontend/api';
import type { MutationFunction } from '@frontend/api';
import { useToast } from '@frontend/components';

import {
  DownloadQuoteFilesQueryData,
  DownloadQuoteFilesQueryVars,
  DownloadQuoteFilesQueryParams,
  QUOTE_FILES_DOWNLOAD_STATUSES,
} from './types';

export const useTriggerQuoteFilesDownload: MutationFunction<
  DownloadQuoteFilesQueryData,
  DownloadQuoteFilesQueryVars,
  [DownloadQuoteFilesQueryParams]
> = (params, { requestOptions, queryOptions } = {}) => {
  const { quoteId } = params;
  const urlPath = `/quotes/${quoteId}/download_files`;
  const request = useRequest(requestOptions);

  return useMutation(
    (requestData) => {
      return request(urlPath, {
        init: {
          method: 'GET',
        },
        query: requestData,
      });
    },
    {
      ...queryOptions,
      mutationKey: ['quotes', 'quoteFiles', 'downloadQuoteFiles'],
    },
  );
};

export const useDownloadQuoteFiles = ({
  quoteId,
}: DownloadQuoteFilesQueryParams) => {
  const toast = useToast();

  return useTriggerQuoteFilesDownload(
    {
      quoteId,
    },
    {
      queryOptions: {
        onError: () => {
          toast({
            status: 'error',
            title: "Couldn't download files from quote",
          });
        },
        onMutate: () => {
          toast({
            status: 'info',
            title: 'Please wait...',
            duration: 2000,
          });
        },
        onSuccess: (data: DownloadQuoteFilesQueryData) => {
          switch (data.status) {
            case QUOTE_FILES_DOWNLOAD_STATUSES.READY: {
              if (data.downloadUrl) {
                toast({
                  status: 'info',
                  title: 'Download started',
                  duration: 2000,
                });
                download(data.downloadUrl);
              } else {
                toast({
                  status: 'error',
                  title: "Couldn't download files from quote",
                  description: data.message,
                });
              }
              break;
            }
            case QUOTE_FILES_DOWNLOAD_STATUSES.UPLOADING: {
              toast({
                status: 'info',
                title: data.message || 'The zip file will be emailed to you.',
              });
              break;
            }
            case QUOTE_FILES_DOWNLOAD_STATUSES.MISSING: {
              toast({
                status: 'error',
                title: "Couldn't download files from quote",
                description: data.message,
              });
              break;
            }
          }
        },
      },
    },
  );
};
